import React, { useCallback, useState } from 'react';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionActions from '@material-ui/core/AccordionActions';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { AccordionDetails, Box, TextField } from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { AttachMoney } from '@material-ui/icons';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { NumberFormatCustom } from '../../../../components/NumberFormatCustom';
import getValidationErrors, {
  IErrors,
} from '../../../../utils/getValidationErrors';
import { IService } from '../../../../shared/interfaces/service-interfaces';
import {
  useDeleteService,
  useUpdateService,
} from '../../../../hooks/data/services';
import LoadingButton from '../../../../components/LoadingButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(2),
    },
    button: {
      borderRadius: '50%',
      minWidth: '0',
      width: '30px',
      height: '30px',
      margin: 'auto',
    },
  }),
);

type IProps = {
  service: IService;
};

const ServiceRow: React.FC<IProps> = ({ service }) => {
  const classes = useStyles();

  const [data, setData] = useState<Omit<IService, 'id'>>(service);
  const [expanded, setExpanded] = useState(false);

  const [errors, setErrors] = useState<IErrors>({
    service_name: '',
    time_box: '',
    price: '',
  });

  const updateService = useUpdateService({ serviceId: service.id });
  const deleteService = useDeleteService({ serviceId: service.id });

  const handleChangeAccordion = (
    event: React.ChangeEvent<{}>,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: '',
    });
  };

  const handleSubmit = useCallback(async () => {
    try {
      const schema = Yup.object().shape({
        service_name: Yup.string().required('Nome é obrigatório'),
        time_box: Yup.number().min(1, 'Duração deve ser maior que 1'),
        price: Yup.string(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await updateService.mutateAsync({
        ...data,
      });

      setErrors({});
      setExpanded(false);
    } catch (err) {
      console.log(err);
      if (err instanceof Yup.ValidationError) {
        const validationErrors = getValidationErrors(err);

        setErrors(validationErrors);

        return;
      }

      toast.error('Erro no cadastro do serviço');
    }
  }, [data, updateService]);

  return (
    <div className={classes.root}>
      <Accordion expanded={expanded} onChange={handleChangeAccordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1c-content"
          id="panel1c-header"
        >
          <Box width="100%">
            <Typography style={{ fontSize: '1.1em' }}>
              {`${service.service_name}`}
            </Typography>
            <Box display="flex" width="100%">
              <AttachMoney />
              <Typography style={{ marginRight: '16px' }}>
                {service.price ?? '-'}
              </Typography>
              <AccessTimeIcon />
              <Typography style={{ marginLeft: '4px' }}>
                {`${service.time_box} min`}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <TextField
              autoFocus
              margin="dense"
              id="service_name"
              name="service_name"
              label="Nome"
              type="text"
              fullWidth
              variant="outlined"
              value={data.service_name}
              onChange={handleChange}
              helperText={errors.service_name}
              error={!!errors.service_name}
            />
            <TextField
              margin="dense"
              id="time_box"
              name="time_box"
              label="Duração (em min)"
              type="number"
              fullWidth
              variant="outlined"
              value={data.time_box > 0 ? data.time_box : ''}
              onChange={handleChange}
              helperText={errors.time_box}
              error={!!errors.time_box}
            />
            <TextField
              margin="dense"
              id="formatted-numberformat-input"
              label="Valor do serviço"
              fullWidth
              variant="outlined"
              name="price"
              value={data.price}
              onChange={handleChange}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
            />
          </Box>
        </AccordionDetails>
        <Divider />
        <AccordionActions>
          <LoadingButton
            size="small"
            variant="text"
            onClick={() => deleteService.mutate({})}
            loading={deleteService.isLoading}
          >
            Excluir
          </LoadingButton>
          <LoadingButton
            size="small"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            loading={updateService.isLoading}
          >
            Salvar
          </LoadingButton>
        </AccordionActions>
      </Accordion>
    </div>
  );
};

export { ServiceRow };
